import React, {useEffect, useMemo, useState} from "react";
import {ArrowDownCircle, ArrowUpCircle, Clock, CloudOff, Delete, FileText, Loader, Play, Printer, Trash2} from "lucide-react";
import {
    applyKey, bigAmountClass, fieldClass, formatMoney, Group, KEYS, money, SectionLabel, Sheet, SheetButton,
} from "./PosModals";
import {FUNDS_CATEGORIES, FUNDS_MAX_AMOUNT} from "./posApi";
import {ticketRef} from "./posReceipt";

// Fonctions de caisse reprises de la caisse desktop (electron-pos) :
// vente libre (VenteLibreSection), prix manuel (PriceKeypadModal), ventes en
// attente (PendingSalesScreen), mouvements de fonds (FundsMovementModal) et
// tickets de la session (ActivityScreen, réimpression).

const rowBorder = (i) => (i ? "border-t border-black/[0.06] dark:border-white/10" : "");

const timeOf = (ms) => new Date(ms).toLocaleTimeString("fr-FR", {hour: "2-digit", minute: "2-digit"});

// ── Pavé numérique ──────────────────────────────────────────────────────────

// value : chaîne saisie ("12.5"). Le clavier physique marche aussi, sauf
// quand le focus est dans un champ texte (libellé, motif…).
export const AmountPad = ({value, onChange, placeholder = "0,00"}) => {
    useEffect(() => {
        const handler = (e) => {
            if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target?.tagName)) return;
            let key = null;
            if (/^[0-9]$/.test(e.key)) key = e.key;
            else if (e.key === "." || e.key === ",") key = ".";
            else if (e.key === "Backspace") key = "⌫";
            else if (e.key === "Delete") key = "C";
            if (!key) return;
            e.preventDefault();
            onChange((prev) => applyKey(prev, key));
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onChange]);

    return (
        <div className="space-y-2.5">
            <div className={`${bigAmountClass} ${value ? "" : "text-gray-400"}`} aria-live="polite" data-testid="amount-display">
                {value || placeholder} <span className="text-lg font-medium text-gray-400">DH</span>
            </div>
            <div className="grid grid-cols-4 gap-2">
                {KEYS.map((k) => {
                    const isClear = k === "C";
                    const isFn = k === "⌫" || k === ".";
                    return (
                        <button key={k} type="button" onClick={() => onChange((p) => applyKey(p, k))}
                                aria-label={k === "⌫" ? "Effacer le dernier chiffre" : isClear ? "Tout effacer" : `Touche ${k}`}
                                className={`${k === "00" || k === "0" ? "col-span-2" : ""} h-12 rounded-[14px] flex items-center justify-center tabular-nums select-none transition-colors ${
                                    isClear ? "bg-red-500/10 text-red-600 text-lg font-semibold active:bg-red-500/20"
                                        : isFn ? "bg-primary/[0.12] text-primary text-2xl active:bg-primary/25"
                                            : "bg-[#E9E9EB] dark:bg-gray-700 text-gray-900 dark:text-white text-2xl active:bg-[#D1D1D6] dark:active:bg-gray-600"}`}>
                            {k === "⌫" ? <Delete className="w-5 h-5"/> : k}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

// ── Vente libre ─────────────────────────────────────────────────────────────

export const FreeItemModal = ({onConfirm, onClose}) => {
    const [label, setLabel] = useState("");
    const [value, setValue] = useState("");
    const amount = Number(value) || 0;
    const submit = () => amount > 0 && onConfirm(label.trim() || "Vente libre", amount);
    return (
        <Sheet title="Vente libre" subtitle="Article hors catalogue (sans TVA)" onClose={onClose} size="sm"
               tone="bg-[#F2F2F7] dark:bg-gray-900" bodyClass="p-4 space-y-4"
               footer={<>
                   <SheetButton variant="secondary" onClick={onClose}>Annuler</SheetButton>
                   <SheetButton variant="primary" disabled={!(amount > 0)} onClick={submit}>Ajouter au panier</SheetButton>
               </>}>
            <label className="block">
                <SectionLabel>Libellé</SectionLabel>
                <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={80}
                       onKeyDown={(e) => e.key === "Enter" && submit()}
                       placeholder="Vente libre" className={fieldClass}/>
            </label>
            <div>
                <SectionLabel>Montant TTC</SectionLabel>
                <AmountPad value={value} onChange={setValue}/>
            </div>
        </Sheet>
    );
};

// ── Prix manuel d'une ligne ─────────────────────────────────────────────────

export const PriceModal = ({line, onConfirm, onReset, onClose}) => {
    const [value, setValue] = useState("");
    const price = Number(value);
    const valid = value !== "" && Number.isFinite(price) && price >= 0;
    const edited = line.basePrice !== undefined && line.basePrice !== line.price;
    return (
        <Sheet title="Modifier le prix" subtitle={line.name} onClose={onClose} size="sm"
               tone="bg-[#F2F2F7] dark:bg-gray-900" bodyClass="p-4 space-y-3"
               footer={<>
                   {edited
                       ? <SheetButton variant="secondary" onClick={onReset}>Prix catalogue</SheetButton>
                       : <SheetButton variant="secondary" onClick={onClose}>Annuler</SheetButton>}
                   <SheetButton variant="primary" disabled={!valid} onClick={() => onConfirm(Math.round(price * 100) / 100)}>
                       Appliquer
                   </SheetButton>
               </>}>
            <Group>
                <div className="flex justify-between px-4 py-3 text-[15px]">
                    <span className="text-gray-600 dark:text-gray-300">Prix actuel (unitaire)</span>
                    <span className="font-semibold tabular-nums text-gray-900 dark:text-white">{formatMoney(line.price)}</span>
                </div>
                {edited && (
                    <div className={`flex justify-between px-4 py-3 text-[15px] ${rowBorder(1)}`}>
                        <span className="text-gray-600 dark:text-gray-300">Prix catalogue</span>
                        <span className="tabular-nums text-gray-500">{formatMoney(line.basePrice)}</span>
                    </div>
                )}
            </Group>
            <AmountPad value={value} onChange={setValue} placeholder={money(line.price)}/>
        </Sheet>
    );
};

// ── Ventes en attente ───────────────────────────────────────────────────────

export const HoldSaleModal = ({defaultLabel, total, onConfirm, onClose}) => {
    const [label, setLabel] = useState("");
    const submit = () => onConfirm(label.trim() || defaultLabel);
    return (
        <Sheet title="Mettre en attente" subtitle={`Ticket de ${formatMoney(total)}`} onClose={onClose} size="sm"
               tone="bg-[#F2F2F7] dark:bg-gray-900" bodyClass="p-4"
               footer={<>
                   <SheetButton variant="secondary" onClick={onClose}>Annuler</SheetButton>
                   <SheetButton variant="primary" onClick={submit}><Clock className="w-4 h-4"/> Mettre en attente</SheetButton>
               </>}>
            <label className="block">
                <SectionLabel>Nom du ticket (optionnel)</SectionLabel>
                <input autoFocus value={label} onChange={(e) => setLabel(e.target.value)} maxLength={40}
                       onKeyDown={(e) => e.key === "Enter" && submit()}
                       placeholder={defaultLabel} className={fieldClass}/>
            </label>
            <p className="mt-3 px-1 text-xs text-gray-500 dark:text-gray-400">
                Le ticket est envoyé au serveur : il est visible et reprenable depuis toutes les caisses du compte.
            </p>
        </Sheet>
    );
};

export const PendingSalesModal = ({held, cartEmpty, loading = false, error = false, onResume, onDelete, onClose}) => (
    <Sheet title="Tickets en attente" subtitle={`${held.length} ticket${held.length > 1 ? "s" : ""} · toutes caisses`}
           onClose={onClose} side bodyClass="p-2.5">
        {loading && (
            <p className="mx-1.5 mb-2 flex items-center gap-1.5 text-xs text-gray-500">
                <Loader className="w-3.5 h-3.5 animate-spin"/> Actualisation…
            </p>
        )}
        {error && (
            <p className="mx-1.5 mb-2 px-3 py-2 rounded-xl bg-red-500/10 text-red-600 text-xs">
                Serveur injoignable : seuls les tickets de ce navigateur sont affichés.
            </p>
        )}
        {!cartEmpty && held.length > 0 && (
            <p className="mx-1.5 mb-2 px-3 py-2 rounded-xl bg-orange-500/10 text-orange-700 dark:text-orange-400 text-xs">
                Le panier en cours sera mis en attente à la reprise d'un ticket.
            </p>
        )}
        <ul className="space-y-1.5">
            {held.map((h) => (
                <li key={h.id} className="rounded-[14px] bg-[rgba(120,120,128,0.08)] px-3 py-2.5">
                    <div className="flex items-baseline gap-2">
                        <span className="flex-1 min-w-0 text-[15px] font-semibold text-gray-900 dark:text-white truncate">{h.label}</span>
                        <span className="text-[15px] font-bold tabular-nums text-gray-900 dark:text-white">{formatMoney(h.total)}</span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {timeOf(h.createdAt)} · {h.sale.lines.length} article{h.sale.lines.length > 1 ? "s" : ""}
                        {h.sale.client ? ` · ${h.sale.client.name}` : ""}
                        {h.device ? ` · ${h.device}` : ""}
                    </p>
                    {!h.remote && h.unsent && (
                        <p className="mt-1 inline-flex items-center gap-1 px-2 py-[2px] rounded-full bg-orange-500/10 text-orange-700 dark:text-orange-400 text-[11px] font-semibold">
                            <CloudOff className="w-3 h-3"/> Pas encore envoyé
                        </p>
                    )}
                    <div className="flex gap-1.5 mt-2">
                        <button type="button" onClick={() => onResume(h)}
                                className="flex-1 h-9 rounded-xl bg-primary text-white text-sm font-semibold flex items-center justify-center gap-1.5 hover:bg-primary-dark">
                            <Play className="w-3.5 h-3.5"/> Reprendre
                        </button>
                        <button type="button" onClick={() => onDelete(h)} aria-label={`Supprimer ${h.label}`}
                                className="w-9 h-9 rounded-xl bg-red-500/10 text-red-600 flex items-center justify-center hover:bg-red-500/20">
                            <Trash2 className="w-4 h-4"/>
                        </button>
                    </div>
                </li>
            ))}
            {held.length === 0 && (
                <li className="px-4 py-12 text-center text-sm text-gray-400">Aucun ticket en attente</li>
            )}
        </ul>
    </Sheet>
);

// ── Mouvement de fonds ──────────────────────────────────────────────────────

const MOTIFS = {
    sortie: ["Achat d'eau", "Achat café", "Fournitures bureau", "Paiement livreur", "Remboursement client"],
    entree: ["Apport fond de caisse", "Correction d'erreur", "Remboursement fournisseur", "Ajout de liquidités"],
};

// balance : espèces théoriques en caisse (fond + ventes + mouvements).
export const FundsMovementModal = ({balance, saving, onConfirm, onClose}) => {
    const [type, setType] = useState("sortie");
    const [value, setValue] = useState("");
    const [category, setCategory] = useState("");
    const [motif, setMotif] = useState("");
    const amount = Math.round((Number(value) || 0) * 100) / 100;

    const error = useMemo(() => {
        if (!(amount > 0)) return null;
        if (amount > FUNDS_MAX_AMOUNT) return `Maximum autorisé : ${formatMoney(FUNDS_MAX_AMOUNT)}`;
        if (type === "sortie" && amount > balance) return `Solde insuffisant (${formatMoney(balance)} en caisse)`;
        return null;
    }, [amount, balance, type]);
    const valid = amount > 0 && !error && motif.trim().length >= 3;

    const switchType = (t) => {
        setType(t);
        setCategory("");
    };

    return (
        <Sheet title="Entrée / sortie d'espèces" subtitle={`En caisse : ${formatMoney(balance)}`} onClose={onClose}
               size="md" busy={saving} tone="bg-[#F2F2F7] dark:bg-gray-900" bodyClass="p-4 space-y-4"
               footer={<>
                   <SheetButton variant="secondary" onClick={onClose} disabled={saving}>Annuler</SheetButton>
                   <SheetButton variant={type === "sortie" ? "destructiveSolid" : "primary"} loading={saving} disabled={!valid}
                                onClick={() => onConfirm({type, amount, category, motif: motif.trim()})}>
                       {type === "sortie" ? "Enregistrer la sortie" : "Enregistrer l'entrée"}
                   </SheetButton>
               </>}>
            <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-[rgba(120,120,128,0.12)]" role="radiogroup" aria-label="Type">
                {[["sortie", "Sortie", ArrowUpCircle], ["entree", "Entrée", ArrowDownCircle]].map(([t, label, Icon]) => (
                    <button key={t} type="button" role="radio" aria-checked={type === t} onClick={() => switchType(t)}
                            className={`h-10 rounded-[10px] text-sm font-semibold flex items-center justify-center gap-1.5 transition ${
                                type === t
                                    ? `bg-white dark:bg-gray-700 shadow ${t === "sortie" ? "text-red-600" : "text-green-600"}`
                                    : "text-gray-600 dark:text-gray-300"}`}>
                        <Icon className="w-4 h-4"/> {label}
                    </button>
                ))}
            </div>

            <div>
                <SectionLabel>Montant</SectionLabel>
                <AmountPad value={value} onChange={setValue}/>
                {error && <p className="mt-2 px-1 text-sm text-red-600">{error}</p>}
            </div>

            <label className="block">
                <SectionLabel>Catégorie</SectionLabel>
                <select value={category} onChange={(e) => setCategory(e.target.value)} className={fieldClass}>
                    <option value="">Sans catégorie</option>
                    {FUNDS_CATEGORIES[type].map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
            </label>

            <label className="block">
                <SectionLabel>Motif</SectionLabel>
                <input value={motif} onChange={(e) => setMotif(e.target.value)} maxLength={200}
                       placeholder="Au moins 3 caractères" className={fieldClass}/>
            </label>
            <div className="flex flex-wrap gap-1.5">
                {MOTIFS[type].map((m) => (
                    <button key={m} type="button" onClick={() => setMotif(m)}
                            className="px-3 py-1.5 rounded-full bg-white dark:bg-gray-800 ring-1 ring-black/[0.06] dark:ring-white/10 text-xs font-medium text-gray-700 dark:text-gray-200 hover:ring-primary/60">
                        {m}
                    </button>
                ))}
            </div>
        </Sheet>
    );
};

// ── Tickets de la session ───────────────────────────────────────────────────

// entries : [{kind: "sale", receipt} | {kind: "movement", id, type, amount, motif, date}]
export const SessionActivityModal = ({entries, invoiceLoadingId, onPrint, onInvoice, onClose}) => {
    const salesCount = entries.filter((e) => e.kind === "sale").length;
    return (
        <Sheet title="Activité de la session" subtitle={`${salesCount} vente${salesCount > 1 ? "s" : ""} depuis ce navigateur`}
               onClose={onClose} side bodyClass="p-2.5">
            <Group>
                {entries.map((e, i) => (e.kind === "sale" ? (
                    <div key={e.receipt.saleId} className={`flex items-center gap-2 px-3 py-2.5 ${rowBorder(i)}`}>
                        <span className="flex-1 min-w-0">
                            <span className="block text-[15px] font-medium text-gray-900 dark:text-white truncate">
                                #{ticketRef(e.receipt.saleId)}{e.receipt.clientName ? ` · ${e.receipt.clientName}` : ""}
                            </span>
                            <span className="block text-xs text-gray-500 dark:text-gray-400">
                                {timeOf(e.receipt.date)} · {e.receipt.payments.map((p) => p.name).join(" + ") || "—"}
                            </span>
                        </span>
                        <span className="text-[15px] font-semibold tabular-nums text-gray-900 dark:text-white">{money(e.receipt.totals.total)}</span>
                        <button type="button" onClick={() => onPrint(e.receipt)} title="Réimprimer le ticket" aria-label="Réimprimer le ticket"
                                className="w-8 h-8 rounded-full flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                            <Printer className="w-4 h-4"/>
                        </button>
                        <button type="button" onClick={() => onInvoice(e.receipt.saleId)} title="Facture PDF" aria-label="Facture PDF"
                                disabled={invoiceLoadingId === e.receipt.saleId}
                                className="w-8 h-8 rounded-full flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40">
                            <FileText className="w-4 h-4"/>
                        </button>
                    </div>
                ) : (
                    <div key={e.id} className={`flex items-center gap-2 px-3 py-2.5 ${rowBorder(i)}`}>
                        {e.type === "sortie"
                            ? <ArrowUpCircle className="w-4 h-4 text-red-500 flex-shrink-0"/>
                            : <ArrowDownCircle className="w-4 h-4 text-green-600 flex-shrink-0"/>}
                        <span className="flex-1 min-w-0">
                            <span className="block text-[15px] text-gray-900 dark:text-white truncate">{e.motif}</span>
                            <span className="block text-xs text-gray-500 dark:text-gray-400">
                                {timeOf(e.date)} · {e.type === "sortie" ? "Sortie" : "Entrée"} d'espèces
                            </span>
                        </span>
                        <span className={`text-[15px] font-semibold tabular-nums ${e.type === "sortie" ? "text-red-600" : "text-green-600"}`}>
                            {e.type === "sortie" ? "−" : "+"}{money(e.amount)}
                        </span>
                    </div>
                )))}
                {entries.length === 0 && (
                    <p className="px-4 py-12 text-center text-sm text-gray-400">Aucune opération dans cette session</p>
                )}
            </Group>
        </Sheet>
    );
};
